import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Eye,
  Search,
  Download,
  RefreshCw,
  ArrowRight,
  Calendar
} from "lucide-react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTransactionHistory } from "../../hooks/useTransactionHistory";
import { getTransactionSummary } from "../../store/slices/transactionSlice";
import TransactionDetailsModal from "../../components/Transaction/TransactionDetailsModal";
import DateRangeFilter from "../../components/ui/DateRangeFilter";
import Pagination from "../../components/tables/Pagination";
import Loader from "../../components/Loader";

export default function TransactionHistory() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { transactionSummary, summaryLoading } = useSelector((state) => state.transaction);
  
  const {
    transactions,
    meta,
    loading,
    filters,
    stats,
    updateFilter,
    resetFilters,
    refreshTransactions,
    onApplyFilter
  } = useTransactionHistory();

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);

  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const itemsPerPage = filters.per_page || 15;

  const handlePageChange = (page) => {
    updateFilter('page', page);
    navigate(`?page=${page}`);
  };

  // Client-side date filtering is now done by backend via filter parameter
  // No need for client-side date filtering anymore

  // Use server-side pagination metadata
  const totalRecords = meta?.total || 0;
  const totalPages = meta?.last_page || 1;
  const validPage = Math.min(Math.max(1, currentPage), totalPages);
  
  // For display, use the transactions from current page (already paginated from server)
  const paginatedTransactions = transactions;

  const handleViewTransaction = async (transactionUuid) => {
    setSelectedTransactionId(transactionUuid);
    setModalOpen(true);
    // Fetch detailed transaction data
    await dispatch(getTransactionSummary(transactionUuid));
  };

  const handleContinueTransaction = (transactionUuid) => {
    navigate(`/business/transactions?uuid=${transactionUuid}`);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedTransactionId(null);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'FULFILLED':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'CANCELLED':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'INITIATED':
        return <Clock className="h-5 w-5 text-blue-600" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'FULFILLED': return 'text-green-600 bg-green-50 border-green-200';
      case 'CANCELLED': return 'text-red-600 bg-red-50 border-red-200';
      case 'INITIATED': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionAmount = (transaction) => {
    return parseFloat(transaction.bill_amount || 0);
  };

  const getFinalAmount = (transaction) => {
    return parseFloat(transaction.final_amount || 0);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="theme-card rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Clock className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Transaction History</h1>
              <p className="text-gray-600 mt-1">View and manage all your transactions</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {/* <button className="theme-button-primary px-4 py-2 text-sm flex items-center space-x-2">
              <Download className="h-4 w-4" />
              <span>Export</span>
            </button> */}
            <button 
              onClick={refreshTransactions}
              disabled={loading}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50"
            >
              <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="theme-card rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Transactions</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <Clock className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="theme-card rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.totalRevenue)}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Date Filter - Compact like BusinessReports */}
      <div className="theme-card rounded-lg p-6">
        <div className="flex items-center gap-2 text-gray-600 mb-3">
          <span className="text-xs font-medium uppercase whitespace-nowrap">Filter by Date:</span>
        </div>
        <DateRangeFilter
          selectedFilter={filters.filter}
          onFilterChange={(value) => updateFilter('filter', value)}
          customDates={filters.customDates}
          onCustomDatesChange={(value) => updateFilter('customDates', value)}
          onApplyFilter={onApplyFilter}
        />
      </div>

      {/* Search */}
      <div className="theme-card rounded-lg p-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by customer, mobile, or transaction ID..."
            value={filters.search}
            onChange={(e) => updateFilter('search', e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
          />
        </div>
      </div>

      {/* Transactions List */}
      <div className="theme-card rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader size="lg" />
            <span className="ml-3 text-gray-600">Loading transactions...</span>
          </div>
        ) : paginatedTransactions.length === 0 ? (
          <div className="text-center py-12">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">No transactions found for the selected filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reference ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mobile
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bill Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer Discount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Final Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Platform Commission
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Settlement
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedTransactions.map((transaction, idx) => (
                  <tr key={transaction.uuid} className="hover:bg-gray-50">
                    {/* Sl no */}
                    <td className="px-6 py-4 text-gray-400 font-mono text-xs whitespace-nowrap">
                      {idx + 1}
                    </td>

                    {/* Reference ID */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-mono text-xs text-blue-700 bg-blue-50 px-2 py-0.5 rounded">
                        {transaction.reference_id}
                      </span>
                    </td>

                    {/* Customer */}
                    <td className="px-6 py-4 font-medium text-gray-800 whitespace-nowrap">
                      {transaction.customer_name || 'N/A'}
                    </td>

                    {/* Mobile */}
                    <td className="px-6 py-4 text-gray-600 font-mono whitespace-nowrap">
                      {transaction.mobile || 'N/A'}
                    </td>

                    {/* Bill Amount */}
                    <td className="px-6 py-4 font-semibold text-gray-900 whitespace-nowrap">
                      {formatCurrency(getTransactionAmount(transaction))}
                    </td>

                    {/* Customer Discount */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-green-700 font-medium">
                        {formatCurrency(transaction.customer_discount || 0)}
                      </span>
                      <span className="text-xs text-gray-400 ml-1">
                        (
                        {transaction.bill_amount
                          ? ((transaction.customer_discount || 0) / transaction.bill_amount * 100).toFixed(1)
                          : 0}
                        %)
                      </span>
                    </td>

                    {/* Final Amount */}
                    <td className="px-6 py-4 font-bold text-emerald-700 whitespace-nowrap">
                      {formatCurrency(getFinalAmount(transaction))}
                    </td>

                    {/* Date */}
                    <td className="px-6 py-4 text-gray-500 whitespace-nowrap text-xs">
                      {formatDate(transaction.date_time)}
                    </td>

                    {/* Platform Commission */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-orange-600 font-medium">
                        {formatCurrency(transaction.platform_commission || 0)}
                      </span>
                      <span className="text-xs text-gray-400 ml-1">
                        (
                        {transaction.bill_amount
                          ? ((transaction.platform_commission || 0) / transaction.bill_amount * 100).toFixed(1)
                          : 0}
                        %)
                      </span>
                    </td>

                    {/* Settlement */}
                    <td className="px-6 py-4">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full border bg-green-50 text-green-600 border-green-200">
                        {transaction.settlement_status || 'Pending'}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button 
                        onClick={() => handleViewTransaction(transaction.uuid)}
                        className="text-blue-600 hover:text-blue-900 flex items-center space-x-1"
                      >
                        <Eye className="h-4 w-4" />
                        <span>View</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={validPage}
          totalPages={totalPages}
          totalItems={totalRecords}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />
      )}

      {/* Transaction Details Modal */}
      <TransactionDetailsModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        transaction={transactionSummary}
        loading={summaryLoading}
      />
    </div>
  );
}